/**
 * The names of the files in a ZIP, read from its central directory (the list of files at the end of
 * the archive) instead of unpacking it, so a ZIP of up to 1 GB is never loaded into memory just to
 * check the names of its pictures. Folders end with "/". Rejects when the file is not a ZIP.
 */
export async function zipEntryNames(file: Blob): Promise<string[]> {
  // The end record is in the last 22 bytes, plus up to 64 KB of archive comment.
  const tailStart = Math.max(0, file.size - (22 + 65535))
  const tail = await readView(file, tailStart, file.size)
  for (let at = tail.byteLength - 22; at >= 0; at--) {
    if (tail.getUint32(at, true) !== 0x06054b50) {
      continue
    }
    const size = tail.getUint32(at + 12, true)
    const offset = tail.getUint32(at + 16, true)
    if (offset !== 0xffffffff && size !== 0xffffffff && tail.getUint16(at + 10, true) !== 0xffff) {
      return readDirectory(file, offset, size)
    }
    // ZIP64: the real numbers are in the ZIP64 end record, which its locator points at.
    if (at < 20 || tail.getUint32(at - 20, true) !== 0x07064b50) {
      throw new Error('Not a readable ZIP.')
    }
    const recordAt = Number(tail.getBigUint64(at - 12, true))
    const record = await readView(file, recordAt, recordAt + 56)
    if (record.getUint32(0, true) !== 0x06064b50) {
      throw new Error('Not a readable ZIP.')
    }
    return readDirectory(
      file,
      Number(record.getBigUint64(48, true)),
      Number(record.getBigUint64(40, true))
    )
  }
  throw new Error('Not a ZIP.')
}

async function readDirectory(file: Blob, offset: number, size: number): Promise<string[]> {
  const dv = await readView(file, offset, offset + size)
  const names: string[] = []
  let at = 0
  while (at + 46 <= dv.byteLength && dv.getUint32(at, true) === 0x02014b50) {
    const nameLength = dv.getUint16(at + 28, true)
    const utf8 = (dv.getUint16(at + 8, true) & 0x800) !== 0
    names.push(text(dv, at + 46, nameLength, utf8))
    at += 46 + nameLength + dv.getUint16(at + 30, true) + dv.getUint16(at + 32, true)
  }
  return names
}

function text(dv: DataView, at: number, length: number, utf8: boolean): string {
  let chars = ''
  for (let i = 0; i < length; i++) {
    chars += String.fromCharCode(dv.getUint8(at + i))
  }
  if (!utf8) {
    return chars
  }
  try {
    return decodeURIComponent(escape(chars))
  } catch {
    return chars
  }
}

function readView(file: Blob, start: number, end: number): Promise<DataView> {
  const part = file.slice(start, end)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new DataView(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(part)
  })
}
